/**
 * 65.49 step 2: the anti-dropship missile in play — the seeker's lock on the unit holding it, the launch from its muzzle
 * along its aim, and the flight (core/homing.ts flies it; ANTI_DROPSHIP_MISSILE_DIALS). The world as the homing core asks
 * it: an enemy ship's hull (its body's middle) and its motion, the sight (personLineOfSight: the ground, the horizon, smoke),
 * and how near a stretch of flight passes the ship's parts (shipDamage.ts shipPass). Ships are the flights in the air and
 * the lighters' runs; a parked ship or a falling one is no target.
 *
 * Nothing here runs unless a unit carries the weapon (a loadout names `missile`): the lock is kept only while the weapon is
 * up, and the missiles' list exists from the first launch, so a match without it is bit-identical.
 */
import type { GameState, MissileFlight, MissileLock, PlayerState, StructureState, Vec3 } from '../types';
import { ANTI_DROPSHIP_MISSILE_DIALS, MISSILE_CLASSES } from '../constants/weapons';
import { AIR_DEFENCE_SITE } from '../constants/action';
import { HELI } from '../constants/helicopter';
import { launchHoming, stepHoming, stepLock, type HomingTarget, type HomingWorld, type LockCandidate } from './homing';
import { shipShapes } from './shipBody';
import { shipNow } from './shipNow';
import { shipPass, shipContact, firstShipHit } from './shipDamage';
import { LIGHTER_ID_BASE } from './lighterBody';
import { isDecoyId, decoyAt, segmentPass } from './decoys';
import { personLineOfSight, personGroundHit } from './fineTerrain';
import { aimDirection, muzzlePoint } from './bullets';
import { headingOf, turnHeading } from './surfaceWalk';
import { weaponOf } from './weapons';
import { explosionBlastFor } from './explosions';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecScale, vecSub, vecNormalize } from '../math';

const D = ANTI_DROPSHIP_MISSILE_DIALS;
/** 69.50 step 2a: a missile's class's dials (constants MISSILE_CLASSES; absent: the small, the anti-dropship missile's). */
export const missileDials = (m: Pick<MissileFlight, 'cls'>) => MISSILE_CLASSES[m.cls ?? 'small'];
const personOf = (state: Pick<GameState, 'config'>): number => PERSON_BODY_HEIGHT * personSetOf(state).k;

/** The middle of ship `id`'s hull at `tick` (its first part's centre: the dropship's body, the lighter's hull), or null. */
function hullOf(state: GameState, id: number, planetIndex: number, tick: number): Vec3 | null {
  for (const sh of shipShapes(state, planetIndex, tick)) {
    if (sh.ref.id !== id) continue;
    const p = sh.prims[0];
    return p && !p.cyl ? p.c : null;
  }
  return null;
}

/** Ship `id`'s hull and velocity at `tick` (world units, a second): a dropship's flight in the air or a lighter's run on the planet (69.17 step 1: as shipNow has it); null for anything else. */
export function shipMotion(state: GameState, id: number, planetIndex: number, tick: number): HomingTarget | null {
  const now = shipNow(state, id, tick);
  if (!now || now.planetIndex !== planetIndex || (now.flight && !now.flight.kin)) return null;
  const pos = hullOf(state, id, planetIndex, tick);
  return pos ? { pos, vel: now.vel } : null;
}

/** 69.59 step 3 (DECISIONS 286 (4)): the share of an IR seeker's lock reach ship `id` is locked within — HELI.irSuppressor for a helicopter flying with its suppressors fitted, else 1 (the hand seeker's and the air-defence site's alike). */
export function irReachShare(state: Pick<GameState, 'dropships'>, id: number): number {
  return state.dropships?.flights.find(f => f.id === id)?.irSuppressor ? HELI.irSuppressor : 1;
}

/** The ships the seeker may hold for `team` on the planet at `tick`: the enemy's flights and lighters, by their hulls. */
export function lockCandidates(state: GameState, team: number, planetIndex: number, tick: number): LockCandidate[] {
  const out: LockCandidate[] = [];
  for (const f of state.dropships?.flights ?? []) {
    if (f.team === team || f.planetIndex !== planetIndex) continue;
    const pos = hullOf(state, f.id, planetIndex, tick);
    if (pos) out.push({ id: f.id, pos, ...(f.irSuppressor ? { reach: irReachShare(state, f.id) } : {}) });   // (69.59 step 3: its IR suppressors fitted)
  }
  for (const r of state.lighters?.runs ?? []) {
    if (r.team === team || r.planetIndex !== planetIndex) continue;
    const pos = hullOf(state, LIGHTER_ID_BASE + r.id, planetIndex, tick);
    if (pos) out.push({ id: LIGHTER_ID_BASE + r.id, pos });
  }
  return out;
}

/** Where a unit's missile leaves and which way (its muzzle, along its aim: the rifle's, core/bullets.ts fireBullet, without the spread). */
export function missileAim(state: GameState, u: PlayerState): { from: Vec3; dir: Vec3 } {
  const ps = personSetOf(state), n = vecNormalize(u.surfaceDir);
  const heading = u.aimYaw ? turnHeading(headingOf(u), n, u.aimYaw) : headingOf(u);
  return { from: muzzlePoint(u, state.planets, ps.eyeHeight, ps), dir: aimDirection(n, heading, u.aimPitch ?? 0) };
}

/** Whether the unit holds the weapon up and ready: in hand, drawn, not reloading, alive, standing on a planet. */
export function missileUp(u: PlayerState): boolean {
  return weaponOf(u) === 'missile' && u.switchLeft === undefined && u.reloadLeft === undefined && u.health > 0 && u.planetIndex >= 0 && !u.airborne;
}

/** The unit's seeker after a tick of `dt`: undefined (absent) unless the weapon is up; else stepLock's over the enemy's ships. */
export function stepMissileLock(state: GameState, u: PlayerState, dt: number, tick: number): MissileLock | undefined {
  if (!missileUp(u)) return undefined;
  const { from, dir } = missileAim(state, u);
  return stepLock(u.missileLock ?? { state: 'search', target: null, held: 0 }, true, from, dir, lockCandidates(state, u.teamIndex, u.planetIndex, tick),
    (a, b) => personLineOfSight(a, b, state), D.lock, dt, personOf(state));
}

/** 69.21: the seeker of a missile mount (the missile technical's) for its seated gunner — as one holding the weapon up, along the gunner's aim from its hatch. */
export function stepMountLock(state: GameState, u: PlayerState, dt: number, tick: number): MissileLock {
  const { from, dir } = missileAim(state, u);
  return stepLock(u.missileLock ?? { state: 'search', target: null, held: 0 }, true, from, dir, lockCandidates(state, u.teamIndex, u.planetIndex, tick),
    (a, b) => personLineOfSight(a, b, state), D.lock, dt, personOf(state));
}

/** A missile from `u`'s muzzle along its aim, chasing the ship it has locked (none locked: it flies straight). */
export function launchMissile(state: GameState, u: PlayerState): { state: GameState; missile: MissileFlight } {
  const { from, dir } = missileAim(state, u);
  const target = u.missileLock?.state === 'lock' ? u.missileLock.target : null;
  const id = state.missiles?.next ?? 0;
  const missile: MissileFlight = { ...launchHoming(from, dir, target, D.homing, personOf(state)), id, ownerId: u.id, team: u.teamIndex, planetIndex: u.planetIndex };
  return { state: { ...state, missiles: { live: [...(state.missiles?.live ?? []), missile], next: id + 1 } }, missile };
}

/**
 * 69.50 step 2a: a missile of the air-defence site's class (AIR_DEFENCE_SITE.missileClass) out of its launcher's cells at
 * `from`, after ship `target` (a ship's id, as the seeker holds it): it leaves toward `toward` raised by half (the ship
 * where the radar has it) and its seeker turns it on. Its owner the site's, its side `team`.
 */
export function launchSiteMissile(state: GameState, site: Pick<StructureState, 'id' | 'ownerId' | 'planetIndex' | 'surfaceDir'>, team: number, target: number, from: Vec3, toward: Vec3): { state: GameState; missile: MissileFlight } {
  const cls = AIR_DEFENCE_SITE.missileClass, d = MISSILE_CLASSES[cls];
  const dir = vecNormalize(vecAdd(vecNormalize(vecSub(toward, from)), vecScale(vecNormalize(site.surfaceDir), 0.5)));
  const id = state.missiles?.next ?? 0;
  const missile: MissileFlight = { ...launchHoming(from, dir, target, d.homing, personOf(state)), id, ownerId: site.ownerId, team, planetIndex: site.planetIndex, ...(cls !== 'small' ? { cls } : {}), site: site.id };
  return { state: { ...state, missiles: { live: [...(state.missiles?.live ?? []), missile], next: id + 1 } }, missile };
}

/** The missile's burst (the rocket's circle at its class's damage: the small's ANTI_DROPSHIP_MISSILE_DIALS.damage), for core/rocket.ts rocketBlast. */
export function missileBlast(state: Pick<GameState, 'config'>, m?: Pick<MissileFlight, 'cls'>): { radius: number; damage: number } {
  return { ...explosionBlastFor(personSetOf(state), 'law')!, damage: m ? missileDials(m).damage : D.damage };
}

/** What became of a missile this tick: it burst (on the ship it chased, a ship it flew into, or the ground), or it was spent. */
export type MissileEnd =
  | { kind: 'burst'; missile: MissileFlight; point: Vec3; on: 'target' | 'ship' | 'ground' }
  | { kind: 'spent'; missile: MissileFlight };

/**
 * The missiles a tick of `dt` on (ships posed at `tick`): each flown by the homing core; one that passes within its fuse of
 * its target, flies into any ship's body (armed after its coast) or into the ground bursts there; one past its life is
 * spent (it falls dead). The list without them and what ended.
 */
export function stepMissiles(state: GameState, dt: number, tick: number): { state: GameState; ends: MissileEnd[] } {
  const live = state.missiles?.live;
  if (!live?.length) return { state, ends: [] };
  const person = personOf(state), sees = (a: Vec3, b: Vec3) => personLineOfSight(a, b, state);
  const kept: MissileFlight[] = [], ends: MissileEnd[] = [];
  for (const m of live) {
    // (69.58: a missile decoyed chases its flare — a closed-form fall, met by its fuse as a point)
    const world: HomingWorld = {
      target: id => (isDecoyId(id) ? decoyAt(state, id, tick) : shipMotion(state, id, m.planetIndex, tick)),
      sees,
      pass: (a, b, id) => { if (!isDecoyId(id)) return shipPass(a, b, m.planetIndex, state, id); const d = decoyAt(state, id, tick); return d ? segmentPass(a, b, d.pos) : Infinity; },
    };
    const dials = missileDials(m).homing;   // (69.50 step 2a: by its class)
    const r = stepHoming(m, dials, world, dt, person);
    const next: MissileFlight = { ...m, ...r.body };
    // (a fuse's hit bursts where it met the ship — its hull, not the missile's own place, up to the fuse off it: a hit is a hit)
    if (r.hit !== undefined) { ends.push({ kind: 'burst', missile: next, point: (isDecoyId(r.hit) ? null : shipContact(m.pos, next.pos, m.planetIndex, state, r.hit)) ?? next.pos, on: 'target' }); continue; }
    if (r.spent) { ends.push({ kind: 'spent', missile: next }); continue; }
    const armed = next.age >= dials.coast;
    const ship = armed ? firstShipHit(m.pos, next.pos, m.planetIndex, state) : null;
    if (ship) { ends.push({ kind: 'burst', missile: next, point: vecAdd(m.pos, vecScale(vecSub(next.pos, m.pos), ship.t)), on: 'ship' }); continue; }
    const ground = personGroundHit(next.pos, state);
    if (ground) { ends.push({ kind: 'burst', missile: next, point: ground.point, on: 'ground' }); continue; }
    kept.push(next);
  }
  return { state: { ...state, missiles: { live: kept, next: state.missiles!.next } }, ends };   // (the list stays, empty, for the next id)
}
